import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { env } from '@/config/env';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render errors below the router shell so one bad widget
 * does not blank the whole admin chrome.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = { error: null };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (env.isDev) {
      console.error('ErrorBoundary caught', error, info.componentStack);
    }
  }

  public override render(): ReactNode {
    const { error } = this.state;

    if (error === null) {
      return this.props.children;
    }

    return (
      <div
        className="flex flex-col items-center justify-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-6 py-12 text-center"
        role="alert"
      >
        <h2 className="text-base font-semibold">
          {this.props.fallbackTitle ?? 'This section crashed'}
        </h2>
        <p className="max-w-md text-sm text-muted-foreground">
          {error.message || 'An unexpected rendering error occurred.'}
        </p>
        {env.isDev && error.stack ? (
          <pre className="max-h-48 w-full max-w-2xl overflow-auto rounded-md bg-muted p-3 text-left text-xs">
            {error.stack}
          </pre>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            this.setState({ error: null });
          }}
        >
          Try again
        </Button>
      </div>
    );
  }
}
